/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { LoggingWindow as SageLoggingWindow } from "@tivocorp/sage-web-sdk";

import { Sage } from "./Sage";
import { PendingTelemetryStore } from "./PendingTelemetryStore";
import { SageIdentityGate } from "./SageIdentityGate";
import { LoggingWindow } from "./LoggingWindow";
import type { LogDetails } from "../types";
import { DiagnosticSeverity } from "../types";

export class DiagnosticLogger {
  public static setDiagnosticLoggingWindow(window: LoggingWindow | null): void {
    if (
      window === null ||
      window.getEndEpochSec() <= window.getStartEpochSec()
    ) {
      Sage.setBackhaulWindow(null);
      return;
    }

    const sdkWindow = new SageLoggingWindow(
      window.getStartEpochSec() * 1000,
      window.getEndEpochSec() * 1000
    );
    Sage.setBackhaulWindow(sdkWindow);
  }

  public static log(
    name: string,
    message: string,
    severity: DiagnosticSeverity,
    details?: LogDetails
  ): void {
    if (SageIdentityGate.isReady()) {
      Sage.logDiagnostics(name, message, severity, details);
      return;
    }
    // Fire-and-forget — never block the caller
    PendingTelemetryStore.enqueueDiagnostic(
      name,
      message,
      severity,
      details
    ).catch(() => {
      // enqueue() already logs its own error; nothing else to do
    });
  }

  public static markSageIdentityReady(): void {
    SageIdentityGate.markReady();
  }

  public static isSageIdentityReady(): boolean {
    return SageIdentityGate.isReady();
  }

  /** Marks Sage identity ready, then flushes pending telemetry. */
  public static async onSageIdentityAvailable(): Promise<void> {
    await SageIdentityGate.onSageIdentityAvailable();
  }

  /** Reset static state — test-only. */
  public static resetForTest(): void {
    SageIdentityGate.resetForTest();
  }
}
